<script setup lang="ts">
import {computed, nextTick, onMounted, PropType, ref, watch} from 'vue';
import {
    CabPeriod,
    CabReportItem,
    KpiStatusEnum,
    MijlpaalEnum,
    OnderwerpEnum_name,
    PeriodEnum,
    renderCabPeriod,
    renderTerseDate,
    SELECTOR_IS_NOT_NULL,
    StatusEnum,
} from 'common';
import {ElBadge, ElButton, ElDialog, ElMessage} from 'element-plus';
import {BackendError, getDocumentaryProof, listObjects, updateObject} from '@/base/callBackend.js';
import {done, showHideLoading} from '@/base/loadingState.js';
import {badgeOffset, downloadFile, localStorageGetJson, localStorageSetJson, SelectGroupInfo} from '@/base/utils.js';
import {newOverallNum} from '@/base/acceptant.js';
import SelectorGroup from '@/views/shared/SelectorGroup.vue';
import ReportItemStrip from '@/views/shared/ReportItemStrip.vue';
import KpiIcon from '@/views/icons/KpiIcon.vue';
import AttachmentIcon from '@/views/icons/AttachmentIcon.vue';

const props = defineProps({
    status: {
        type    : Number as PropType<StatusEnum>,
        required: true,
    },
});

//============================================================================================================================================
const all                      = ref<CabReportItem[]>([]);
const allNaInstellingen        = computed<CabReportItem[]>(() => all.value.filter(ri => selectedInstellingenList.value.includes(ri.organization[0].uuid)));
const allNaThemas              = computed<CabReportItem[]>(() => allNaInstellingen.value.filter(ri => selectedThemasList.value.includes(ri.theme[0].uuid)));
const allNaOnderwerpen         = computed<CabReportItem[]>(() => allNaThemas.value.filter(ri => selectedOnderwerpenList.value.includes(ri.kpi[0].onderwerp?.toString() || '')));
const allNaDate                = computed<CabReportItem[]>(() => allNaOnderwerpen.value.filter(ri => selectedDate.value === renderIndienMoment(ri)));
//
const selectedInstellingenList = ref<string[]>([]);
const selectedThemasList       = ref<string[]>([]);
const selectedOnderwerpenList  = ref<string[]>([]);
const selectedDate             = ref<string>();
//
const visibleSelectedItems     = computed<CabReportItem[]>(() => allNaDate.value.filter(ri => selectedItems.value.has(ri.uuid)));
//
const numAll                   = computed<number>(() => all.value.length);
const numAtInstellingMap       = computed<Map<string, number>>(() => makeNumberMap(instellingGetter, all.value));
const numAtThemaMap            = computed<Map<string, number>>(() => makeNumberMap(themaGetter, allNaInstellingen.value));
const numAtOnderwerpMap        = computed<Map<string, number>>(() => makeNumberMap(onderwerpGetter, allNaThemas.value));
const numAtDateMap             = computed<Map<string, number>>(() => makeNumberMap(dateGetter, allNaOnderwerpen.value));
const isEmpty                  = computed<boolean>(() => 0 == Array.from(numAtDateMap.value.values()).reduce((sum, value) => sum + value, 0));
//
const dateList                 = computed<string[]>(() => [...new Set(all.value.map(ri => renderIndienMoment(ri)))].sort((a, b) => a.localeCompare(b)));
const viewReportItem           = computed<CabReportItem | undefined>(() => editIndex.value != undefined ? allNaDate.value[editIndex.value] : undefined);
const cursorLine               = ref<number>();
const editIndex                = ref<number>();
//
const dialogVisible            = ref<boolean>();
const viewLoading              = ref<boolean>();

// Selection state for checkboxes
const selectedItems = ref<Set<string>>(new Set());

// this will set the badge on the tab:
watch(numAll, (n) => {
    newOverallNum(props.status, n);
});
watch(numAtDateMap, () => {
    if (isEmpty.value) {
        nextTick(() => selectedDate.value = undefined);
    } else {
        const numAtSel = selectedDate.value == undefined ? 0 : numAtDateMap.value.get(selectedDate.value) ?? 0;
        if (numAtSel == 0) {
            nextTick(() => selectedDate.value = dateList.value.find(date => numAtDateMap.value.get(date) ?? 0 > 0));
        }
    }
}, {deep: true});
watch(selectedDate, (d) => {
    localStorageSetJson(`${props.status}:selectedDate`, d);
});
watch(dateList, (l) => {
    if (!selectedDate.value) {
        const saved        = localStorageGetJson(`${props.status}:selectedDate`);
        selectedDate.value = saved && l.includes(saved) ? saved : l[0];
    }
});
onMounted(async () => {
    showHideLoading(true);
    viewLoading.value = true;
    await getAllFromBackend();
    viewLoading.value = false;
    showHideLoading(false);
});

async function getAllFromBackend() {
    try {
        const kpiReportItems: CabReportItem[]      = await listObjects(CabReportItem, {
            isMijlpaal      : false,
            status          : props.status,
            kpiStatus       : KpiStatusEnum.gehaald,
            vecozoId        : SELECTOR_IS_NOT_NULL,
            uitkeringsbedrag: SELECTOR_IS_NOT_NULL,
        });
        const mijlpaalReportItems: CabReportItem[] = await listObjects(CabReportItem, {
            isMijlpaal      : true,
            status          : props.status,
            reportedValue   : MijlpaalEnum.gehaald,
            vecozoId        : SELECTOR_IS_NOT_NULL,
            uitkeringsbedrag: SELECTOR_IS_NOT_NULL,
        });
        all.value                                  = [... kpiReportItems, ... mijlpaalReportItems].sort((a, b) => a.compare(b));
    } catch (e) {
        console.error(e);
        if (e instanceof BackendError) {
            ElMessage.error(`Error: ${e.details}`);
        }
    }
}

//============================================================================================================================================
function makeNumberMap(f: (_ri: CabReportItem) => string, riList: CabReportItem[]): Map<string, number> {
    return new Map(
        riList.reduce((acc, ri) => {
            const s = f(ri);
            if (s) {
                acc.set(s, (acc.get(s) || 0) + 1);
            }
            return acc;
        }, new Map<string, number>()),
    );
}

//==================================================================================================
function instellingSelector(ri: CabReportItem): SelectGroupInfo {
    const x = ri.organization[0];
    return {
        name: x.name,
        id  : x.uuid,
    };
}

function themaSelector(ri: CabReportItem): SelectGroupInfo {
    const x = ri.theme[0];
    return {
        name: x.name,
        id  : x.uuid,
    };
}

function onderwerpSelector(ri: CabReportItem): SelectGroupInfo {
    const x = ri.kpi[0].onderwerp!;
    return {
        name: OnderwerpEnum_name(x),
        id  : x.toString(),
    };
}

//==================================================================================================
function instellingGetter(ri: CabReportItem): string {
    return ri.organization[0].uuid;
}

function themaGetter(ri: CabReportItem): string {
    return ri.theme[0].uuid;
}

function onderwerpGetter(ri: CabReportItem): string {
    return ri.kpi[0].onderwerp?.toString() || '';
}

function dateGetter(ri: CabReportItem): string {
    return renderIndienMoment(ri);
}

//==================================================================================================
function getInstellingBadgeValue(a: string) {
    return numAtInstellingMap.value.get(a as string)!;
}

function getThemaBadgeValue(a: string) {
    return numAtThemaMap.value.get(a as string)!;
}

function getOnderwerpBadgeValue(a: string) {
    return numAtOnderwerpMap.value.get(a)!;
}

function getDateBadgeValue(a: string) {
    return numAtDateMap.value.get(a)!;
}

//============================================================================================================================================

function dateTabClicked(n: string) {
    if (numAtDateMap.value.get(n)) {
        selectedDate.value = n;
    }
}

//============================================================================================================================================
function renderIndienMoment(ri: CabReportItem) {
    if (ri.isMijlpaal) {
        return renderCabPeriod(CabPeriod.of(ri.meetMoment!, PeriodEnum.Q).previousPeriod());
    } else {
        return renderCabPeriod(ri.period[0].qPeriod());
    }
}

function dynLineClass(i: number): string {
    const onEvenLine   = i % 2 == 0;
    const onCursorLine = i == cursorLine.value;
    return onEvenLine ? onCursorLine ? 'bg-green-300' : 'bg-green-200' : onCursorLine ? 'bg-green-300' : 'bg-green-100';
}

function getBadgeValue(n: string): number | undefined {
    return done ? getDateBadgeValue(n) : undefined;
}

function getBadgeColor(): string {
    return props.status === StatusEnum.submitted ? 'orange' : 'blue';
}

function dynDateTabClass(n: string) {
    if (n === selectedDate.value) {
        return `font-bold bg-white ${dynDateTabClassCursor(n)}`;
    } else if (numAtDateMap.value.get(n)) {
        return `font-this hover:bg-gray-200 ${dynDateTabClassCursor(n)}`;
    } else {
        return `font-normal text-gray-400 ${dynDateTabClassCursor(n)}}`;
    }
}

function dynDateTabClassCursor(n: string) {
    if (n === selectedDate.value) {
        return 'cursor-default';
    } else if (numAtDateMap.value.get(n)) {
        return 'cursor-pointer';
    } else {
        return 'cursor-default';
    }
}

// Select all visible items
function selectAllVisible() {
    selectedItems.value = new Set(allNaDate.value.map(ri => ri.uuid));
}

// Deselect all
function deselectAll() {
    selectedItems.value.clear();
}

// Toggle selection for a single item
function toggleSelection(uuid: string) {
    if (selectedItems.value.has(uuid)) {
        selectedItems.value.delete(uuid);
    } else {
        selectedItems.value.add(uuid);
    }
}

// Check if all visible items are selected
const allVisibleSelected = computed(() =>
    allNaDate.value.length > 0 &&
    allNaDate.value.every(ri => selectedItems.value.has(ri.uuid)),
);

// Toggle select all/none
function toggleSelectAll() {
    if (allVisibleSelected.value) {
        deselectAll();
    } else {
        selectAllVisible();
    }
}

// Change acceptance status for selected items
async function changeSelectedAcceptance(status: StatusEnum) {
    if (selectedItems.value.size !== 0) {
        showHideLoading(true);
        try {
            let numOk  = 0;
            let numErr = 0;
            for (const ri of visibleSelectedItems.value) {
                ri.status = status;
                if (!await updateObject(ri)) {
                    numErr++;
                } else {
                    numOk++;
                    const index = all.value.findIndex(ri => ri.uuid === ri!.uuid);
                    if (index !== -1) {
                        all.value.splice(index, 1); // Remove the item from the list
                    }
                }
            }
            if (numOk > 0) {
                if (status === StatusEnum.accepted) {
                    ElMessage.success(`${numOk} geselecteerde items geaccepteerd.`);
                } else if (status === StatusEnum.rejected) {
                    ElMessage.success(`${numOk} geselecteerde items afgewezen.`);
                } else if (status === StatusEnum.submitted) {
                    ElMessage.success(`${numOk} beoordelingen ongedaan gemaakt.`);
                }
            }
            if (numErr > 0) {
                ElMessage.error(`${numErr} fouten bij wijzigen goedkeuring.`);
            }
        } finally {
            showHideLoading(false);
        }
    }
}

</script>

<template>
    <div class="w-full h-full relative">

        <div class="pt-5">
            <template v-if="!viewLoading">
                <SelectorGroup
                    :all="all"
                    :names="['instelling','instellingen']"
                    :local-storage-name="`${status}:selectedInstellingen`"
                    :select-field="instellingSelector"
                    v-model:selected-list="selectedInstellingenList"
                    :get-badge-value="getInstellingBadgeValue"
                    :badge-color="getBadgeColor()"
                />
                <SelectorGroup
                    :all="all"
                    :names="['thema','themas']"
                    :local-storage-name="`${status}:selectedThemas`"
                    :select-field="themaSelector"
                    v-model:selected-list="selectedThemasList"
                    :get-badge-value="getThemaBadgeValue"
                    :badge-color="getBadgeColor()"
                />
                <SelectorGroup
                    :all="all"
                    :names="['onderwerp','onderwerpen']"
                    :local-storage-name="`${status}:selectedOnderwerpen`"
                    :select-field="onderwerpSelector"
                    v-model:selected-list="selectedOnderwerpenList"
                    :get-badge-value="getOnderwerpBadgeValue"
                    :badge-color="getBadgeColor()"
                />
            </template>
        </div>

        <div class="flex py-3 px-10">
            <!-- Datum selectie labels -->
            <div class="w-44 my-6" v-if="done">
                <div v-for="n in dateList"
                     :key="n"
                     class="pl-2 pr-8 py-4 my-1 text-right"
                     :class="dynDateTabClass(n)"
                     @click="dateTabClicked(n)"
                >
                    <el-badge
                        :value="getBadgeValue(n)"
                        :class="dynDateTabClassCursor(n)"
                        :color="getBadgeColor()"
                        :offset="badgeOffset([10, -4], false, done ? getDateBadgeValue(n) : undefined,  undefined)"
                        badge-class="font-normal"
                    >
                        {{ n }}
                    </el-badge>
                </div>
            </div>

            <!-- Content -->
            <div class="w-full border-8 border-white" v-if="done && !viewLoading">
                <!-- New accept/reject buttons -->
                <div class="m-1 flex items-center gap-2">
                    <el-button
                        @click="toggleSelectAll"
                        :disabled="allNaDate.length === 0"
                        class="w-36"
                    >
                        {{ allVisibleSelected ? 'Deselecteer alles' : 'Selecteer alles' }}
                    </el-button>
                    <div>
                        Selectie:
                        <el-button
                            v-if="props.status === StatusEnum.submitted || props.status === StatusEnum.rejected"
                            type="success"
                            @click="changeSelectedAcceptance(StatusEnum.accepted)"
                            :disabled="visibleSelectedItems.length === 0"
                        >
                            Accepteer
                        </el-button>
                        <el-button
                            v-if="props.status === StatusEnum.submitted || props.status === StatusEnum.accepted"
                            type="danger"
                            @click="changeSelectedAcceptance(StatusEnum.rejected)"
                            :disabled="visibleSelectedItems.length === 0"
                        >
                            Wijs af
                        </el-button>
                        <el-button
                            v-if="props.status === StatusEnum.accepted || props.status === StatusEnum.rejected"
                            type="warning"
                            @click="changeSelectedAcceptance(StatusEnum.submitted)"
                            :disabled="visibleSelectedItems.length === 0"
                        >
                            Maak beoordeling ongedaan
                        </el-button>
                    </div>
                </div>
                <div class="overflow-x-auto">
                    <div v-if="all.length===0"
                         class="font-thin text-gray-500 text-xl text-center m-10 h-72"
                    >
                        - er staan op dit moment geen items open -
                    </div>
                    <div v-else-if="allNaDate.length===0"
                         class="font-thin text-gray-500 text-xl text-center m-10 h-72"
                    >
                        - lege selectie -
                    </div>
                    <table class="table-auto border-collapse border w-full cursor-pointer">
                        <tbody>
                        <tr v-for="(ri,i) in allNaDate"
                            :key="ri.uuid"
                            class=""
                            :class="dynLineClass(i)"
                            @mouseenter="cursorLine = i"
                            @mouseleave="cursorLine = -1"
                            @click="editIndex = i; dialogVisible = true"
                        >
                            <td class="whitespace-nowrap text-center border-y border-gray-300 py-2 px-2"
                                @click.stop
                            >
                                <input
                                    type="checkbox"
                                    :checked="selectedItems.has(ri.uuid)"
                                    @change="toggleSelection(ri.uuid)"
                                    @click.stop
                                />
                            </td>
                            <td class="whitespace-nowrap text-center border-y border-gray-300 py-2 px-3">
                                {{ ri.organization[0].name }}
                            </td>
                            <td class="whitespace-nowrap text-center border-y border-gray-300 py-2 px-3">
                                {{ ri.theme[0].name }}
                            </td>
                            <td class="whitespace-nowrap text-center border-y border-gray-300 py-2 px-3">
                                {{ OnderwerpEnum_name(ri.kpi[0].onderwerp) }}
                            </td>
                            <td class="                  text-left   border-y border-gray-300 py-2 pl-3">
                                <KpiIcon :kpi="ri.kpi[0]"/>
                            </td>
                            <td class="                  text-left   border-y border-gray-300 py-2 pl-0 pr-3 font-bold w-full">
                                {{ ri.kpi[0].name }}
                            </td>
                            <td class="whitespace-nowrap text-center border-y border-gray-300 py-2 px-3">
                                <!-- Paperclip icon link for documentary proof -->
                                <a v-if="ri.documentaryProofName"
                                   href="#"
                                   @click.prevent="getDocumentaryProof(ri.uuid).then((res:Response)=>downloadFile(res, ri.documentaryProofName!))"
                                   title="Download bewijsstuk"
                                   @click.stop
                                >
                                    <AttachmentIcon/>
                                </a>
                            </td>
                        </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    </div>

    <el-dialog
        v-model="dialogVisible"
        width="90%"
        align-center
        destroy-on-close
        :show-close="false"
        v-if="viewReportItem" :draggable="true"
    >
        <template #header>
            <div class="flex flex-row items-center text-lg">
                <div class="mx-2 whitespace-nowrap">
                    {{ viewReportItem.organization[0].name }}
                </div>
                <div class="mx-2 whitespace-nowrap">
                    {{ viewReportItem.theme[0].name }}
                </div>
                <div class="mx-2 whitespace-nowrap">
                    {{ OnderwerpEnum_name(viewReportItem.kpi[0].onderwerp) }}
                </div>
                <KpiIcon :kpi="viewReportItem.kpi[0]" class="ml-2"/>
                <div class="font-bold flex-wrap">
                    {{ viewReportItem.kpi[0].name }}
                </div>
                <div class="flex-1">
                    <!--filler-->
                </div>

                <div class="mx-2 px-4 bg-gray-200 whitespace-nowrap">
                    {{ viewReportItem.isMijlpaal ? `per ${renderTerseDate(viewReportItem.meetMoment)}` : `over ${renderCabPeriod(viewReportItem.period[0])}`
                    }}
                </div>
            </div>
        </template>

        <!-- TODO -->
        <ReportItemStrip
            :flag-omissions="true"
            :item="viewReportItem"
        />

    </el-dialog>
</template>

<style>
/*noinspection CssUnusedSymbol*/
.el-button:disabled {
    @apply bg-gray-400 text-gray-200;
}
</style>
